import React, { useCallback, useEffect, useRef } from "react";
import { Container } from "@pixi/react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  setIntroTransitionActive,
  setUserInterfaceDisabled,
} from "../../../../../../State/actions";
import { attemptFullscreenToggle, safeInit } from "../../../../../../utilities";
import {
  selectIntroPopupActive,
  selectUserInterfaceDisabled,
  selectIsPotCollectionMidBonusReload,
  selectCurrentRound,
  selectLoaderFadedOut,
} from "../../../../../../State/selectors";
import { SOUND_NAME } from "../../../../../../sounds";
import { useDimensions } from "../../../../../../Contexts/DimensionsContext";
import {
  useInitialIdleAnimations,
} from "../../../../../../Hooks/useLandingAnimations";
import { soundManager } from "../../../../../../SoundManager";
import { useSecondaryToolbar } from "../../../../../../Hooks/useSecondaryToolbar";
import { FEATURES_CONFIG } from "../../../../../../featuresConfig";
import IntroPopupComposedController from "./IntroPopupComposedController";
import ButtonSound from "../../../../secondary/Buttons/ButtonSound";

// ─── IntroPopupComposed ───────────────────────────────────────────────────────
// Slim React shell: handles Redux state, click/keyboard events, and lifecycle.
// All PIXI display objects (bg, logo, cards, text) are managed by the controller.
const IntroPopupComposed = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const introPopupActive = useSelector(selectIntroPopupActive);
  const userInterfaceDisabled = useSelector(selectUserInterfaceDisabled);

  const dimensionsContext = useDimensions();
  const { dimensions, device, orientation, uiSymbolSize, isMobileSmall } = dimensionsContext;
  const toolbar = useSecondaryToolbar();
  // Match ToolbarTop: sound button is positioned relative to a centered toolbar.
  // Without this offset, mobile landscape (toolbar narrower than screen) lands left of the real button.
  const toolbarOffsetX = (dimensions.width - toolbar.width) / 2;
  const { triggerInitialIdleAnimations } = useInitialIdleAnimations();
  const isPotCollectionMidBonusReload = useSelector(selectIsPotCollectionMidBonusReload);
  const currentRound = useSelector(selectCurrentRound);
  const loaderFadedOut = useSelector(selectLoaderFadedOut);

  const containerRef = useRef(null);
  const controllerRef = useRef(null);
  // Always holds the latest dimensions — read when the deferred (RAF) controller
  // construction runs, so a resize during the loader fade isn't built from the
  // stale first-render dimensions captured by the init effect's [] closure.
  const dimensionsContextRef = useRef(dimensionsContext);
  dimensionsContextRef.current = dimensionsContext;
  const handleClickRef = useRef(null);
  const handleSwipeFullscreenRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const pendingRafIdsRef = useRef([]);

  // ── Initialize controller once container is available ───────────────────
  useEffect(() => {
    if (containerRef.current && !controllerRef.current) {
      safeInit(() => {
        controllerRef.current = new IntroPopupComposedController(
          containerRef.current,
          dimensionsContextRef.current,
          t,
        );
        // Wire click handler immediately via ref proxy — always calls latest version
        controllerRef.current.setClickHandler((...args) => {
          if (handleClickRef.current) handleClickRef.current(...args);
        });
        // Fullscreen-on-swipe proxy — enters fullscreen without closing the popup
        controllerRef.current.setFullscreenHandler((...args) => {
          if (handleSwipeFullscreenRef.current)
            handleSwipeFullscreenRef.current(...args);
        });
      });
    }
    return () => {
      if (controllerRef.current) {
        controllerRef.current.cleanup();
        controllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Update controller dimensions on resize / orientation change ────────
  useEffect(() => {
    if (controllerRef.current) {
      controllerRef.current.updateDimensions(dimensionsContext);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions.width, dimensions.height, uiSymbolSize, device, orientation, isMobileSmall]);

  // ── Click / tap handler: fullscreen + dismiss popup ─────────────────
  const handleClick = useCallback(
    async (event) => {
      if (debounceTimeoutRef.current) return;
      debounceTimeoutRef.current = setTimeout(() => {
        debounceTimeoutRef.current = null;
      }, 300);

      if (event?.stopPropagation) event.stopPropagation();
      soundManager.play(SOUND_NAME.button_click);
      await attemptFullscreenToggle(dispatch, device);

      const hasWins = currentRound?.win > 0;

      if (FEATURES_CONFIG.POT_COLLECTION.ENABLED && isPotCollectionMidBonusReload) {
        // Mid-bonus reload: base game goes to idle (no win processing).
        // The bonus scene is already mounted (potCollectionActive = true).
        // GameView transition effect will handle POTs, counter, and respins popup.
        triggerInitialIdleAnimations();
      } else if (hasWins) {
        // Win round — skip idle, let win controller handle it
      } else if (FEATURES_CONFIG.SPINE_ANIMATIONS.IDLE_ENABLED) {
        triggerInitialIdleAnimations();
      }

      // Stop intro popup sounds
      soundManager.stop(SOUND_NAME.intro_popup);
      soundManager.stop(SOUND_NAME.bonus_intro_popup);

      // Start background music now that intro is closing
      soundManager.releaseMusicPlayback();

      dispatch(setIntroTransitionActive(true));
    },
    [
      dispatch,
      device,
      triggerInitialIdleAnimations,
      isPotCollectionMidBonusReload,
      currentRound,
    ],
  );

  // ── Keep click handler ref current (avoids stale closures) ─────────────
  handleClickRef.current = handleClick;

  // ── Swipe fullscreen: enter fullscreen (mobile/tablet) without closing ──
  // attemptFullscreenToggle only enters when not already fullscreen, so
  // repeated swipes are safe and never toggle it back off.
  const handleSwipeFullscreen = useCallback(() => {
    attemptFullscreenToggle(dispatch, device);
  }, [dispatch, device]);
  handleSwipeFullscreenRef.current = handleSwipeFullscreen;

  // ── Enable UI when popup becomes active ────────────────────────────────
  useEffect(() => {
    if (introPopupActive) {
      dispatch(setUserInterfaceDisabled(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [introPopupActive]);

  // ── Play card entrance animation + intro sound after loader fades out ───
  useEffect(() => {
    if (introPopupActive && loaderFadedOut && controllerRef.current) {
      controllerRef.current.playIntro();
      // Play intro popup sounds — will silently fail if AudioContext is locked
      soundManager.play(SOUND_NAME.intro_popup);
      soundManager.play(SOUND_NAME.bonus_intro_popup);
    }
  }, [introPopupActive, loaderFadedOut]);

  // ── Cleanup pending RAFs on close ──────────────────────────────────────
  useEffect(() => {
    if (!introPopupActive) {
      pendingRafIdsRef.current.forEach((id) => cancelAnimationFrame(id));
      pendingRafIdsRef.current = [];
    }
  }, [introPopupActive]);

  // ── Spacebar listener (identical to original IntroPopupComposed) ───────
  useEffect(() => {
    const handleKeyDown = (event) => {
      const isSpace =
        event.code === "Space" || event.key === " " || event.keyCode === 32;
      const isLeft =
        event.code === "ArrowLeft" ||
        event.key === "ArrowLeft" ||
        event.keyCode === 37;
      const isRight =
        event.code === "ArrowRight" ||
        event.key === "ArrowRight" ||
        event.keyCode === 39;
      if (!isSpace && !isLeft && !isRight) return;
      if (event.repeat) {
        event.preventDefault();
        if (typeof event.stopPropagation === "function") event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function") event.stopImmediatePropagation();
        return;
      }
      if (!introPopupActive || userInterfaceDisabled) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      // ArrowLeft / ArrowRight move the slider one slide; Space closes.
      if (isLeft) {
        controllerRef.current?.prevSlide();
      } else if (isRight) {
        controllerRef.current?.nextSlide();
      } else {
        handleClick(event);
      }
    };

    if (introPopupActive && !userInterfaceDisabled) {
      document.addEventListener("keydown", handleKeyDown, { capture: true, passive: false });
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [introPopupActive, userInterfaceDisabled, handleClick]);

  // ── Cleanup on unmount ─────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      pendingRafIdsRef.current.forEach((id) => cancelAnimationFrame(id));
      pendingRafIdsRef.current = [];
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      if (controllerRef.current) {
        controllerRef.current.cleanup();
        controllerRef.current = null;
      }
    };
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────
  // Container always mounts; visibility toggled by introPopupActive.
  // Controller manages all PIXI children imperatively.
  return (
    <Container
      ref={containerRef}
      name="intro_composed"
      x={0}
      y={0}
      zIndex={100}
      visible={introPopupActive}
      eventMode={introPopupActive ? "static" : "none"}
      hitArea={{ contains: () => true }}
      sortableChildren={true}
    >
      {/* Sound toggle — same parent offset as ToolbarTop so coords match in-game ButtonSound */}
      {introPopupActive && (
        <Container
          name="intro_sound_button_anchor"
          x={toolbarOffsetX}
          y={0}
          zIndex={200}
        >
          <ButtonSound toolbar={toolbar} zIndex={200} />
        </Container>
      )}
    </Container>
  );
};

export default IntroPopupComposed;
